/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { RouteFullScreenModal } from "../../components/RouteFullScreenModal";
import * as yup from "yup";
import { useSelector } from "react-redux";
import * as authSelectors from "../auth/authSelectors";
import { useNavigate, useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useController, useForm } from "react-hook-form";
import { TextInput } from "../../components/forms/inputs/TextInput";
import { Divider } from "../../components/Divider";
import { SelectField } from "../../components/forms/fields/SelectField";
import { ButtonAction } from "../../components/NewButtons";
import { ActionSpinner } from "../../components/ActionSpinner";
import {
  useAddPaymentProviderToShop,
  useGetAdyenStoresForOrganization,
  useUpdatePaymentProvider,
} from "./service/shop-api";
import {
  MutateShopPaymentProvider,
  PaymentMethodType,
  ShopPaymentProvider,
} from "./models/ShopPaymentProvider";
import { toastr } from "react-redux-toastr";
import { getErrorMessage } from "../../error-handler";
import { SpinnerBlock } from "../common/Spinner";
import { useEffect } from "react";
import { useListPaymentProviderConfigurations } from "../payment-provider-configurations/service/payment-provider-configrations-api";
import { SelectInput } from "../../components/forms/inputs/SelectInput";

export const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-top: 24px;
  max-width: 600px;
  align-self: center;
  padding-bottom: 24px;
  width: 100%;
  align-items: center;
  justify-content: start;
  overflow-y: auto;
  padding-left: 16px;
  padding-right: 16px;
`;

type Props = {
  shopPaymentProvider?: ShopPaymentProvider;
  isLoading?: boolean;
};

export const MutateShopPaymentProviderTemplate = ({
  shopPaymentProvider,
  isLoading,
}: Props) => {
  const { shopId } = useParams();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const navigate = useNavigate();

  const schema = yup.object({
    providerConfigurationId: yup
      .string()
      .required("Provider configuration id is required"),
    statementText: yup
      .string()
      .max(20, "Statement text must be at most 20 characters")
      .required("Statement text is required"),
    paymentPointId: yup.string(),
    paymentMethodTypes: yup
      .array(
        yup
          .string()
          .oneOf([
            "card",
            "applepay",
            "googlepay",
            "vipps_mobile_pay",
            "mobilepay",
            "drinks_account",
            "unspecified",
            "swish",
            "klarna_pay_now",
            "klarna_pay_later",
          ])
      )
      .min(1, "At least one payment method type is required"),
  });

  const form = useForm<yup.InferType<typeof schema>>({
    resolver: yupResolver(schema),
    defaultValues: {
      providerConfigurationId: "",
      statementText: "",
      paymentPointId: "",
      paymentMethodTypes: [],
    },
  });

  const { reset } = form;

  const {
    data: paymentProviderConfigurations,
    isLoading: isLoadingPaymentProviderConfigurations,
  } = useListPaymentProviderConfigurations(organizationKeyName);

  const { data: adyenStores } = useGetAdyenStoresForOrganization(
    organizationKeyName
  );

  const { mutateAsync: addPaymentProviderToShop } = useAddPaymentProviderToShop(
    {
      organizationKeyName,
      shopId,
    }
  );
  const { mutateAsync: updatePaymentProviderToShop } = useUpdatePaymentProvider(
    {
      shopId,
      organizationKeyName,
    }
  );

  const {
    field: { onChange, value, ref, ...rest },
  } = useController({
    control: form.control,
    name: "paymentMethodTypes",
  });

  const paymentMethodOptions = [
    {
      label: "Card",
      value: "card",
    },
    {
      label: "Apple Pay",
      value: "applepay",
    },
    {
      label: "Google Pay",
      value: "googlepay",
    },
    {
      label: "Vipps Mobile Pay",
      value: "vipps_mobile_pay",
    },
    {
      label: "MobilePay",
      value: "mobilepay",
    },
    {
      label: "Top up card",
      value: "drinks_account",
    },
    {
      label: "Swish",
      value: "swish",
    },
    {
      label: "Klarna Pay now",
      value: "klarna_pay_now",
    },
    {
      label: "Klarna Pay later",
      value: "klarna_pay_later",
    },
    {
      label: "Unspecified",
      value: "unspecified",
    },
  ];

  const onSubmit = async (data: yup.InferType<typeof schema>) => {
    const { paymentMethodTypes, paymentPointId, ...rest } = data;
    const payload: MutateShopPaymentProvider & { shopId: string } = {
      ...rest,
      paymentPointId: paymentPointId || "",
      shopId: shopId!,
      paymentMethodTypes: paymentMethodTypes as PaymentMethodType[],
    };
    try {
      if (shopPaymentProvider) {
        await updatePaymentProviderToShop({
          data: {
            ...payload,
            id: shopPaymentProvider.id,
          },
          organizationKeyName,
        });
      } else {
        await addPaymentProviderToShop({
          data: payload,
          organizationKeyName,
        });
      }
      toastr.success(
        "Success",
        `Payment provider ${shopPaymentProvider ? "updated" : "created"}`
      );
      navigate("..");
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  const isAdyen = ["adyen", "adyen_custom"].includes(
    (paymentProviderConfigurations || []).find(
      (x) => x.id === form.watch("providerConfigurationId")
    )?.provider || ""
  );

  useEffect(() => {
    if (!shopPaymentProvider) {
      return;
    }
    reset({
      providerConfigurationId: shopPaymentProvider.provider.id,
      statementText: shopPaymentProvider.statementText,
      paymentPointId: shopPaymentProvider.paymentProviderId,
      paymentMethodTypes: shopPaymentProvider.paymentMethods,
    });
  }, [shopPaymentProvider, reset]);

  return (
    <FormProvider {...form}>
      <RouteFullScreenModal
        title={
          shopPaymentProvider
            ? "Update payment provider"
            : "Add new payment provider"
        }
        actions={
          <ButtonAction
            onClick={form.handleSubmit(onSubmit)}
            label="Save"
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? <ActionSpinner /> : "Save"}
          </ButtonAction>
        }
      >
        <Divider />
        <div css={bodyStyle}>
          {isLoading || isLoadingPaymentProviderConfigurations ? (
            <SpinnerBlock />
          ) : (
            <>
              <SelectInput
                label="Provider configuration id"
                name="providerConfigurationId"
                onChange={(changedValue) => {
                  const chosenProvider = paymentProviderConfigurations?.find(
                    (x) => x.id === (changedValue as string)
                  );
                  if (chosenProvider?.provider !== "drinks_account") {
                    onChange(
                      (value || []).filter((x) => x !== "drinks_account")
                    );
                  }
                }}
                options={
                  paymentProviderConfigurations?.map((x) => ({
                    label:
                      x.providerName === "adyen_unverified"
                        ? "SumUp"
                        : x.providerName,
                    value: x.id,
                  })) || []
                }
              />
              <TextInput label="Statement text" name="statementText" />
              {isAdyen ? (
                <SelectInput
                  label="Payment point id"
                  name="paymentPointId"
                  helperText="Select between the Adyen stores for the organization"
                  options={
                    adyenStores?.map((x) => ({
                      label: x.description,
                      value: x.reference || x.id,
                      isDisabled: !x.reference,
                    })) || []
                  }
                />
              ) : (
                <TextInput label="Payment point id" name="paymentPointId" />
              )}
              <SelectField
                isMulti
                label="Payment method types"
                closeMenuOnSelect={false}
                placeholder="Start typing to search for payment method types..."
                errorMessage={
                  form.getFieldState("paymentMethodTypes", form.formState).error
                    ?.message
                }
                onChange={(value) => {
                  const values = value as {
                    value: PaymentMethodType;
                    label: string;
                  }[];
                  onChange(values.map((x) => x.value));
                }}
                options={paymentMethodOptions
                  .filter((x) => {
                    const currentProvider = form.watch(
                      "providerConfigurationId"
                    );
                    const currentProviderConfiguration = paymentProviderConfigurations?.find(
                      (x) => x.id === currentProvider
                    );

                    if (
                      currentProviderConfiguration?.provider !==
                      "drinks_account"
                    ) {
                      return x.value !== "drinks_account";
                    }

                    return true;
                  })
                  .filter((x) => {
                    const currentProvider = form.watch(
                      "providerConfigurationId"
                    );
                    const currentProviderConfiguration = paymentProviderConfigurations?.find(
                      (x) => x.id === currentProvider
                    );

                    if (
                      currentProviderConfiguration?.provider !== "adyen" &&
                      currentProviderConfiguration?.provider !== "adyen_custom"
                    ) {
                      return (
                        x.value !== "klarna_pay_now" &&
                        x.value !== "klarna_pay_later" &&
                        x.value !== "swish"
                      );
                    }

                    return true;
                  })}
                value={paymentMethodOptions.filter((x) =>
                  (value || []).includes(x.value)
                )}
                innerRef={ref}
                {...rest}
              />
            </>
          )}
        </div>
      </RouteFullScreenModal>
    </FormProvider>
  );
};
